'use client'

import { useEffect, useState, type ComponentProps } from 'react'
import { Check, ChevronDown, Loader2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useLoginState } from '@/hooks/use-login-state'
import { canTransitionEventStatus } from '@/lib/intranet-preview-shared'
import { cn } from '@/lib/utils'
import type { Event } from '@/payload-types'

type EventStatus = NonNullable<Event['event_status']>

type EventStatusBadgeProps = {
  eventId: string
}

const EVENT_STATUS_LABELS: Record<EventStatus, string> = {
  pending: 'Pending',
  participating: 'HeadCount is Coming',
  'not-participating': 'Not Participating',
}

const EVENT_STATUS_VARIANTS: Record<EventStatus, ComponentProps<typeof Badge>['variant']> = {
  pending: 'secondary',
  participating: 'default',
  'not-participating': 'destructive',
}

const EVENT_STATUS_ORDER: EventStatus[] = ['pending', 'participating', 'not-participating']

export function EventStatusBadge({ eventId }: EventStatusBadgeProps) {
  const { isLoading: loginLoading, isLoggedIn, roles, intranetRoles } = useLoginState()
  const [status, setStatus] = useState<Event['event_status'] | undefined>(undefined)
  const [isOpen, setIsOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (loginLoading || !isLoggedIn) {
      setStatus(undefined)
      return
    }

    let cancelled = false

    void (async () => {
      try {
        const response = await fetch(`/api/events/${encodeURIComponent(eventId)}/status`, {
          method: 'GET',
          cache: 'no-store',
          credentials: 'include',
        })

        if (!response.ok) {
          if (!cancelled) setStatus(undefined)
          return
        }

        const data = (await response.json()) as { event_status?: Event['event_status'] }
        if (!cancelled) setStatus(data.event_status ?? null)
      } catch {
        if (!cancelled) setStatus(undefined)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [eventId, loginLoading, isLoggedIn])

  if (loginLoading || !isLoggedIn || status === undefined) {
    return null
  }

  const resolvedStatus = status ?? 'pending'
  const availableStatuses = EVENT_STATUS_ORDER.filter(
    (option) =>
      option === resolvedStatus ||
      canTransitionEventStatus(resolvedStatus, option, roles, intranetRoles),
  )
  const canEdit = availableStatuses.some((option) => option !== resolvedStatus)

  const badge = (
    <Badge variant={EVENT_STATUS_VARIANTS[resolvedStatus]} className="uppercase tracking-wide">
      {EVENT_STATUS_LABELS[resolvedStatus]}
    </Badge>
  )

  if (!canEdit) {
    return badge
  }

  const updateStatus = async (next: EventStatus) => {
    if (next === resolvedStatus) {
      setIsOpen(false)
      return
    }

    setIsSaving(true)
    setError(null)

    try {
      const response = await fetch(`/api/events/${encodeURIComponent(eventId)}/status`, {
        method: 'PATCH',
        cache: 'no-store',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event_status: next }),
      })

      if (!response.ok) {
        const data = (await response.json().catch(() => null)) as { message?: string } | null
        setError(data?.message ?? 'Unable to update status.')
        return
      }

      const data = (await response.json()) as { event_status?: Event['event_status'] }
      setStatus(data.event_status ?? next)
      setIsOpen(false)
    } catch {
      setError('Unable to update status.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger
        type="button"
        className="inline-flex items-center gap-1 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
        aria-label="Change event status"
      >
        {badge}
        <ChevronDown className="size-4 opacity-70" aria-hidden="true" />
      </PopoverTrigger>
      <PopoverContent align="center" className="w-60 p-1">
        <div className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Set status
        </div>
        <div className="flex flex-col">
          {availableStatuses.map((option) => {
            const isActive = option === resolvedStatus
            return (
              <button
                key={option}
                type="button"
                disabled={isSaving}
                onClick={() => void updateStatus(option)}
                className={cn(
                  'flex items-center justify-between rounded-sm px-2 py-2 text-left text-sm transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50',
                  isActive && 'font-semibold',
                )}
              >
                <span>{EVENT_STATUS_LABELS[option]}</span>
                {isActive && <Check className="size-4" aria-hidden="true" />}
              </button>
            )
          })}
        </div>
        {isSaving && (
          <div className="flex items-center gap-2 px-2 py-1.5 text-xs text-muted-foreground">
            <Loader2 className="size-3 animate-spin" aria-hidden="true" />
            Saving…
          </div>
        )}
        {error && <div className="px-2 py-1.5 text-xs text-destructive">{error}</div>}
      </PopoverContent>
    </Popover>
  )
}
